import { useEffect, useState } from 'react';
import { Facebook, MessageCircle, MessageSquare, MapPin, Instagram, Phone, CalendarCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import logoFull from '@/assets/logo-full-white-transparent.png';
import { openAiChat } from '@/lib/ai-chat';
import {
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_TEL_HREF,
  BUSINESS_WHATSAPP_HREF,
  BUSINESS_INSTAGRAM_HREF,
  BUSINESS_TEXT_BODY,
  buildSmsHref,
  isAppleMobile,
} from '@/lib/contact-links';

const whatsappIcon = (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-400">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const optionClass = 'flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 p-4 hover:bg-white/10 transition-all duration-300 text-left w-full';
const iconWrapClass = 'w-12 h-12 rounded-full flex items-center justify-center shrink-0';

export const Footer = () => {
  // iOS and Android expect different separators for a pre-filled text message.
  const [appleDevice, setAppleDevice] = useState(false);
  useEffect(() => {
    setAppleDevice(isAppleMobile(navigator.userAgent, navigator.maxTouchPoints ?? 0));
  }, []);
  const textHref = buildSmsHref(BUSINESS_TEXT_BODY, appleDevice);

  return (
    <footer className="bg-[hsl(var(--color-darkest))] text-white py-16 px-4 md:px-8">
      <div className="container mx-auto">
        {/* Contact options — main-site CTAs scroll here */}
        <div id="contact" className="scroll-mt-24 mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">Get in touch</h2>
          <p className="text-white/70 text-center mb-8">Pick whichever works best for you.</p>
          <div className="grid gap-3 max-w-2xl mx-auto">
            <Link to="/book" className={optionClass}>
              <div className={`${iconWrapClass} bg-accent/20`}>
                <CalendarCheck className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-semibold">Book a 45-minute Zoom call</p>
                <p className="text-sm text-white/60">Pick a time that works for you</p>
              </div>
            </Link>
            <a href={BUSINESS_TEL_HREF} className={optionClass}>
              <div className={`${iconWrapClass} bg-accent/20`}>
                <Phone className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-semibold">Call us: {BUSINESS_PHONE_DISPLAY}</p>
                <p className="text-sm text-white/60">Click to call</p>
              </div>
            </a>
            <a href={textHref} className={optionClass}>
              <div className={`${iconWrapClass} bg-accent/20`}>
                <MessageSquare className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-semibold">Send us a text</p>
                <p className="text-sm text-white/60">Click to send us a message</p>
              </div>
            </a>
            <button type="button" onClick={() => void openAiChat()} className={optionClass}>
              <div className={`${iconWrapClass} bg-accent/20`}>
                <Sparkles className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-semibold">Chat with our AI agent</p>
                <p className="text-sm text-white/60">Instant answers, 24/7</p>
              </div>
            </button>
            <a href={BUSINESS_WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className={optionClass}>
              <div className={`${iconWrapClass} bg-green-500/20`}>
                {whatsappIcon}
              </div>
              <div>
                <p className="font-semibold">WhatsApp us</p>
                <p className="text-sm text-white/60">Chat with us on WhatsApp</p>
              </div>
            </a>
            <a href={BUSINESS_INSTAGRAM_HREF} target="_blank" rel="noopener noreferrer" className={optionClass}>
              <div className={`${iconWrapClass} bg-pink-500/20`}>
                <Instagram className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <p className="font-semibold">Contact us on Instagram</p>
                <p className="text-sm text-white/60">@arno.adornier</p>
              </div>
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <div
              role="img" aria-label="The Alpha Omega Hub"
              className="h-12 w-40 mb-4 bg-white"
              style={{
                WebkitMaskImage: `url(${logoFull})`,
                maskImage: `url(${logoFull})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'left center',
                maskPosition: 'left center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
            <p className="text-white/70 max-w-md mb-4">
              Video ad agency for kitchen remodeling companies and home service businesses across the United States.
            </p>
            <div className="flex items-center gap-4">
              <a href="https://www.facebook.com/thealphaomegahub" target="_blank" rel="noopener noreferrer" aria-label="The Alpha Omega Hub on Facebook" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/arno.adornier" target="_blank" rel="noopener noreferrer" aria-label="Arno Adornier on Instagram" className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center hover:bg-pink-500/30 transition-colors">
                <Instagram className="w-5 h-5 text-pink-400" />
              </a>
              <a href="tel:+18138193701" aria-label="Call The Alpha Omega Hub at +1 (813) 819-3701" className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors">
                <MessageCircle className="w-5 h-5" />
              </a>
              <a href="https://wa.me/18138193701" target="_blank" rel="noopener noreferrer" aria-label="Chat with us on WhatsApp" className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center hover:bg-green-500/30 transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-400">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h2 className="font-semibold mb-4 text-white text-base">Quick Links</h2>
            <ul className="space-y-2">
              <li><Link to="/case-studies" className="text-white/70 hover:text-white transition-colors">Case Studies</Link></li>
              <li><Link to="/#team" className="text-white/70 hover:text-white transition-colors">The Team</Link></li>
              <li><Link to="/blog" className="text-white/70 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/resources" className="text-white/70 hover:text-white transition-colors">Resources</Link></li>
              <li><Link to="/terms" className="text-white/70 hover:text-white transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/book" className="text-white/70 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h2 className="font-semibold mb-4 text-white text-base">Contact</h2>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>Call +1 (813) 819-3701</span>
              </li>
              <li>
                <a href="https://wa.me/18138193701" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>WhatsApp Us</span>
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Serving clients across the United States</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/15 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/60">
            © {new Date().getFullYear()} The Alpha Omega Hub LLC (since 2023). All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
